// The contract's vocabulary (PLAN-CLIENT-SPLIT §2).
//
// **Squares stay packed and branded; everything else is a name, never a
// sentinel.** A square is the engine's packed coordinate — the client gets
// `fileOf`/`rankOf`/`square` from here (§4) and nothing else about its shape.
// A side is a branded number because there can be eight of them. A direction,
// a piece, a structure and a variant are strings, so a client reading a fact
// never has to know that `NO_BELT` is -1 or that a pawn is 1.
//
// **This file imports only `src/shared/`** — `eslint.config.js` makes any
// other import under `src/` an error. The packing is the shared vocabulary's,
// wrapped in the brands.

import {
  fileOf as fileOfPacked,
  type KingKindName,
  rankOf as rankOfPacked,
  square as pack
} from "src/shared/board";

declare const brand: unique symbol;
type Brand<T, B extends string> = T & { readonly [brand]: B };

/** A packed board coordinate: `((rank + 512) << 10) | (file + 512)`. */
export type Square = Brand<number, "Square">;

/**
 * Which army: 0 is White, 1 is Black, and a run can hold up to eight. **Not
 * a facing** (that is `Direction`) and **not a team** — a team is an alliance
 * of sides, given per side as `teams` on the opening command.
 */
export type Side = Brand<number, "Side">;

export type Direction = "N" | "E" | "S" | "W";

/** `monster` is retired and never emitted; kept because the human tree keys a model by it. */
export type PieceName = "pawn" | "knight" | "bishop" | "rook" | "queen" | "king" | "monster";

export type StructureName =
  | "store"
  | "turret"
  | "altar"
  | "relic"
  /** Retired with standing orders (§31 R1): no structure is one. Kept for the human client's tables. */
  | "banner"
  | "keystone"
  | "well"
  /** Retired with the Research Station (§31 R1): no structure is one. Kept for the human client's tables. */
  | "research";

/**
 * The §31 S4–S7 kinds: walls, the Armory, the power network, trains. **Their own
 * union, not `StructureName`'s**, so the human tree's `Record<StructureName, …>`
 * tables stay whole until it adopts them (client ask) — where a fact or a tile
 * has to fit `StructureName` it says `store` and carries the true name in
 * `building`.
 */
export type FactoryStructureName =
  | "simple-wall"
  | "firm-wall"
  | "reinforced-wall"
  | "armory"
  | "coal-generator"
  | "nuclear-generator"
  | "battery"
  | "cabling-tower"
  /** §31 S7. */
  | "track"
  | "train-engine"
  | "train-car"
  /** §31 A6–A9's artifact structures — the Mainframe (A7) is `keystone`. */
  | "fusion-chamber"
  | "shield-generator"
  | "artisan-pillar"
  /** §31 S8's two new turrets; the Laser is `turret`. */
  | "kinetic-turret"
  | "cannon-turret"
  /** §31 A10–A13. */
  | "propaganda-tower"
  | "house-of-cards"
  | "endurance-monument"
  | "fiend-fountain"
  /** §33 T5. */
  | "legion-tower"
  /** §33 I5. */
  | "drone-warehouse"
  | "empower-tower";

/** Every structure's true name. */
export type BuildingName = StructureName | FactoryStructureName;

export type VariantName =
  | "plain"
  | "marshal"
  | "dam"
  | "turbo-bishop"
  | "turbo-rook"
  | "joan"
  | "matriarch"
  | "confessor"
  | "healer"
  | "pegasus"
  | "unicorn"
  | "gryphon"
  | "paladin"
  // The fuses (§31 P4).
  | "dreadnought"
  | "alicorn"
  | "joan-of-arc";

/**
 * The four resources: metal, stone, the specials and fuel. Metal and stone are
 * *ladders* of grades (iron → steel …); special and fuel are resources whose
 * grades are each their own thing. The rules call one a material **line** — an
 * assembly line, not a direction — which is the word the `line` fields on the
 * views keep. The type says resource, which is what it is.
 */
export type ResourceName = "metal" | "stone" | "special" | "fuel";
/** @deprecated The old name for `ResourceName`; the fields it typed still say `line`. */
export type LineName = ResourceName;

/** An army style, as `catalog.armies()` names it: the keys of the shared `KING_KIND` table. */
export type ArmyName = KingKindName;

/** An inclusive rectangle of files and ranks. */
export interface Bounds {
  minFile: number;
  maxFile: number;
  minRank: number;
  maxRank: number;
}

export type { BoardEdge } from "src/shared/board";
/** A zone of the map (§31 D2): "field", "quarry", "golden", "treasure-room" … */
export type { ZoneName } from "src/shared/zones";
export {
  MATERIAL_IDS,
  FUEL_IDS,
  type MaterialId,
  type FuelId,
  type ResourceId
} from "src/shared/industry";

export const square =(file: number, rank: number): Square => pack(file, rank) as Square;
export const fileOf = (sq: Square): number => fileOfPacked(sq);
export const rankOf = (sq: Square): number => rankOfPacked(sq);
